import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';
import { Helpers } from 'src/app/models/helpers';
import { Log } from './log';

/**
 * Forma minima dell'oggetto utente persistito in `localStorage`.
 *
 * Storicamente l'oggetto è stato trattato come `any` ovunque: l'API ne
 * restituisce campi diversi a seconda dell'endpoint (`/login`,
 * `/manufacturer/login`, ecc.). Qui dichiariamo solo i campi che la UI
 * legge in modo ricorrente, e teniamo `[key: string]: unknown` aperto
 * per non rompere usi che non conosciamo ancora. La tipizzazione stretta
 * arriverà in Fase 1.
 */
export interface CurrentUser {
  ac_token?: string;
  ac_user_type_cod?: Helpers.Permission | number;
  ac_email?: string;
  user_ID?: number;
  account_id?: number;
  [key: string]: unknown;
}

const KEY_USER = 'currentUser';
const KEY_TOKEN = 'tokenValue';
const KEY_SCREEN_CREDENTIALS = 'screenCredentials';

export interface ScreenCredentials {
  email: string;
  password: string;
}

/**
 * SessionService — incapsula l'accesso a `localStorage` per i dati di
 * sessione utente (`currentUser` + `tokenValue`).
 *
 * Prima di T8, ~18 file leggevano/scrivevano direttamente
 * `localStorage.getItem('currentUser')` e simili, ognuno con la sua
 * gestione (o non gestione) di JSON malformato. Da T8 questo è l'unico
 * punto di contatto con `localStorage` per questi dati.
 *
 * AuthService rimane il punto pubblico per login/logout: internamente
 * delega qui per la persistenza e per l'osservabilità dello stato.
 *
 * NOTA: cache applicative diverse (ordini, settings stampante,
 * monthlyObjectives, query-search, ecc.) NON sono gestite da
 * SessionService — sono `cache` di dominio, non sessione utente.
 * Eventuale `AppCacheService` o store dedicato verrà valutato in
 * Fase 1.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {

  private readonly _user$ = new BehaviorSubject<CurrentUser | null>(
    this.readUser(),
  );

  /** Stream dell'utente corrente. `null` quando non c'è sessione. */
  readonly user$: Observable<CurrentUser | null> = this._user$.asObservable();

  /**
   * Stream booleano: true se c'è un token salvato in storage.
   * Derivato da `user$`: ogni volta che cambia user, ricalcola.
   */
  readonly isAuthenticated$: Observable<boolean> = this._user$.pipe(
    map(() => this.isAuthenticated()),
    distinctUntilChanged(),
  );

  /**
   * Snapshot sincrono dell'utente corrente. Pensato per guard e
   * interceptor che hanno bisogno del valore subito senza subscribe.
   * Restituisce `null` se non c'è sessione o se il JSON è corrotto.
   */
  getCurrentUser(): CurrentUser | null {
    return this._user$.value;
  }

  /**
   * Aggiorna l'utente corrente. Persiste in `localStorage` e notifica
   * gli observer. Se il valore passato contiene `ac_token`, il token
   * separato in `tokenValue` viene allineato.
   */
  setCurrentUser(user: CurrentUser | null): void {
    if (user == null) {
      this.clear();
      return;
    }
    try {
      localStorage.setItem(KEY_USER, JSON.stringify(user));
    } catch (e) {
      Log.error('SessionService: impossibile salvare currentUser', e);
    }
    if (user.ac_token) {
      this.setToken(user.ac_token);
    }
    this._user$.next(user);
  }

  /** Token raw da `localStorage`. `null` se assente. */
  getToken(): string | null {
    return localStorage.getItem(KEY_TOKEN);
  }

  setToken(token: string): void {
    localStorage.setItem(KEY_TOKEN, token);
  }

  /**
   * True se c'è un token salvato. NON valida il token (non controlla
   * scadenza né signature): è solo presenza.
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Pulisce la sessione: rimuove `currentUser` e `tokenValue` da
   * `localStorage`, notifica gli observer.
   *
   * NOTA: non tocca `screenCredentials` di proposito. Quelle servono
   * a `AuthService.reloginScreen()` per far ripartire da solo
   * l'account Screen (kiosk) dopo un token scaduto: se le rimuovessimo
   * qui, il primo logout forzato le cancellerebbe rendendo impossibile
   * il re-login automatico successivo.
   */
  clear(): void {
    localStorage.removeItem(KEY_USER);
    localStorage.removeItem(KEY_TOKEN);
    this._user$.next(null);
  }

  /**
   * Salva le credenziali di un account Screen (kiosk) per permettere
   * il re-login automatico silenzioso quando il token scade (vedi
   * `AuthInterceptor` + `AuthService.reloginScreen()`). Da usare solo
   * per account con `ac_user_type_cod === Helpers.PermisionScreen`.
   */
  setScreenCredentials(credentials: ScreenCredentials): void {
    try {
      localStorage.setItem(KEY_SCREEN_CREDENTIALS, JSON.stringify(credentials));
    } catch (e) {
      Log.error('SessionService: impossibile salvare screenCredentials', e);
    }
  }

  getScreenCredentials(): ScreenCredentials | null {
    const raw = localStorage.getItem(KEY_SCREEN_CREDENTIALS);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as ScreenCredentials;
    } catch (e) {
      Log.warn('SessionService: screenCredentials in localStorage non parsabili, reset', e);
      localStorage.removeItem(KEY_SCREEN_CREDENTIALS);
      return null;
    }
  }

  /**
   * Lettura iniziale dell'utente dallo storage, con gestione difensiva
   * del JSON corrotto. Se il JSON non parsa, lo rimuove (non vogliamo
   * lasciare valori illeggibili che fanno fallire ogni lettura
   * successiva).
   */
  private readUser(): CurrentUser | null {
    const raw = localStorage.getItem(KEY_USER);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as CurrentUser;
      }
      return null;
    } catch (e) {
      Log.warn(
        'SessionService: currentUser in localStorage non parsabile, reset',
        e,
      );
      localStorage.removeItem(KEY_USER);
      return null;
    }
  }
}
